import ArrowRight16 from '@carbon/icons-react/es/arrow--right/16';
import { Link, useNavigate } from '@rocketlane/rocket-router';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Space } from 'antd';
import React, { FC, Suspense } from 'react';
import { FormattedMessage } from 'react-intl';
import { UserAPI } from 'api/user';
import { Form } from 'components/atoms/form';
import {
  Container,
  Heading,
  LinkText,
  UserInfo,
} from 'components/atoms/form-elements';
import { Layout } from 'components/atoms/layout';
import { PageLoading } from 'components/atoms/loading';
import { Button } from 'components/facade/button';
import { Input } from 'components/facade/input';
import { toastService } from 'components/molecules/toast';
import { TokenErrorStatus } from 'components/organisms/token-error/constant';
import { RoutePaths } from 'constant';
import { useQueryParams } from 'hooks/use-query-params';
import { useTranslation } from 'hooks/use-translation';
import { CompanyLogo } from 'routes/auth/common';
import { getPasswordFormErrorMessage } from 'utils/error';
import { getUserLabel } from 'utils/get-user-label';
import { getTokenMeta } from 'utils/token';

const TokenError = React.lazy(() =>
  import('components/organisms/token-error/token-error').then((d) => ({
    default: d.TokenError,
  })),
);

enum PasswordsFields {
  New = 'newPassword',
  Confirm = 'confirmPassword',
}

export const ResetPasswordPage: FC<React.PropsWithChildren<unknown>> = () => {
  const history = useNavigate();
  const params = useQueryParams();
  const urlToken = params.get('token') || '';
  const { data, isLoading } = useQuery(
    ['get-details-from-token', urlToken],
    () => UserAPI.getDetailsFromToken(urlToken),
    {
      retry: false,
      refetchOnWindowFocus: false,
    },
  );

  const { isExpired, decoded } = getTokenMeta(urlToken);

  const updateProfile = (values) => {
    return UserAPI.resetPassword({
      newPassword: values[PasswordsFields.New] || '',
      confirmPassword: values[PasswordsFields.Confirm] || '',
      token: urlToken || '',
    })
      .then(() => {
        history(RoutePaths.login);
      })
      .catch((error) => Promise.reject(error));
  };

  const passwordMatchText = useTranslation('form.validations.password_match');
  const errorTitle = useTranslation('error.generic.title');
  const errorMessageFallback = useTranslation('rte.error');
  const passwordValidationUnified = useTranslation(
    'password.validation.unified',
  );

  const { mutateAsync: sendResetMail, isLoading: resetMailSending } =
    useMutation(updateProfile);

  const onFinish = (values: any) => {
    sendResetMail(values).catch((error: any) => {
      const { primary, secondary } = getPasswordFormErrorMessage(
        error,
        passwordValidationUnified,
        errorMessageFallback,
      );

      toastService({
        title: errorTitle,
        message: primary,
      }).error();

      if (secondary != null) {
        toastService({
          title: errorTitle,
          message: secondary,
        }).error();
      }
    });
  };

  const pwdPlaceholder = useTranslation('password.placeholder');

  if (decoded && isExpired) {
    return (
      <Suspense fallback={<PageLoading />}>
        <TokenError
          status={TokenErrorStatus.Expired}
          email={decoded.emailId}
          resendEmail={() => UserAPI.triggerResetPasswordEmail(decoded.emailId)}
          linkType={decoded.linkType}
        />
      </Suspense>
    );
  }

  if (!decoded) {
    return (
      <Suspense fallback={<PageLoading />}>
        <TokenError status={TokenErrorStatus.Invalid} />
      </Suspense>
    );
  }

  return isLoading ? (
    <PageLoading />
  ) : (
    <Layout>
      <Container>
        <Space direction="vertical" size={16}>
          {data ? (
            <CompanyLogo data={data?.sessionPayload.user.company} />
          ) : null}
          <Heading>
            <FormattedMessage
              id="reset_password.page_title"
              values={{
                user: data?.sessionPayload.user
                  ? getUserLabel(data?.sessionPayload.user)
                  : '',
              }}
            />
          </Heading>
          <Form layout="vertical" name="reset-password" onFinish={onFinish}>
            <Form.Item
              name={PasswordsFields.New}
              rules={[
                {
                  required: true,
                  message: (
                    <FormattedMessage id="form.validations.new_password" />
                  ),
                },
                {
                  min: 11,
                  message: <FormattedMessage id="password.help" />,
                  validateTrigger: 'onBlur',
                },
              ]}
              label={<FormattedMessage id="new_password" />}
            >
              <Input
                autoComplete="new-password"
                type="password"
                placeholder={pwdPlaceholder}
                name="new-password"
              />
            </Form.Item>
            <Form.Item
              name={PasswordsFields.Confirm}
              label={<FormattedMessage id="confirm_password" />}
              rules={[
                {
                  required: true,
                  message: (
                    <FormattedMessage id="form.validations.confirm_password" />
                  ),
                },
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    if (!value || getFieldValue('newPassword') === value) {
                      return Promise.resolve();
                    }

                    return Promise.reject(passwordMatchText);
                  },
                }),
              ]}
            >
              <Input
                autoComplete="confirm-password"
                type="password"
                placeholder={pwdPlaceholder}
                name="confirm-password"
              />
            </Form.Item>
            <Form.Item>
              <Button
                htmlType="submit"
                loading={resetMailSending}
                type="primary"
                icon={<ArrowRight16 />}
                iconPlacement="right"
              >
                <FormattedMessage id="reset_password.cta" />
              </Button>
            </Form.Item>
            <UserInfo>
              <FormattedMessage
                id="reset_password.cta_back"
                values={{
                  a: (chunks) => (
                    <Link to={RoutePaths.login}>
                      <LinkText>{chunks}</LinkText>
                    </Link>
                  ),
                }}
              />
            </UserInfo>
          </Form>
        </Space>
      </Container>
    </Layout>
  );
};
